import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '../../components/ui/dialog';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../components/ui/table';
import { Loader2, Plus, Search, GraduationCap, Trash2, Edit2, Upload, FileText } from 'lucide-react';
import { getLearningCatalogue, createContent, updateContent, deleteContent, uploadAsset } from '../../services/api';
import { toast } from 'sonner';

const AdminLearningPage = () => {
    const [learningItems, setLearningItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');

    // Form dialog
    const [dialogOpen, setDialogOpen] = useState(false);
    const [formLoading, setFormLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [formData, setFormData] = useState({
        title: '',
        category: '',
        module: '',
        contentType: 'TEXT',
        content: '',
        url: ''
    });

    useEffect(() => {
        fetchLearningData();
    }, []);

    const fetchLearningData = async () => {
        setLoading(true);
        try {
            const response = await getLearningCatalogue();
            const data = response.data;

            // The API return structure from backend/routes/content.py is:
            // return {"categories": items} where items are from query_items(CONTENT_TABLE, "TYPE#LEARNING")
            // These items are flat in the table.

            if (data.categories && Array.isArray(data.categories)) {
                setLearningItems(data.categories);
            } else {
                setLearningItems([]);
            }
        } catch (error) {
            console.error('Failed to fetch learning data:', error);
            toast.error('Failed to load learning materials');
        } finally {
            setLoading(false);
        }
    };

    const filteredItems = learningItems.filter((item) =>
        item.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.category?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.module?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const handleOpenAdd = () => {
        setEditingItem(null);
        setFormData({
            title: '',
            category: '',
            module: '',
            contentType: 'TEXT',
            content: '',
            url: ''
        });
        setDialogOpen(true);
    };

    const handleOpenEdit = (item) => {
        setEditingItem(item);
        setFormData({
            title: item.title,
            category: item.category || '',
            module: item.module || '',
            contentType: item.contentType || (item.url ? 'PDF' : 'TEXT'),
            content: item.content || '',
            url: item.url || ''
        });
        setDialogOpen(true);
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (file.type !== 'application/pdf') {
            toast.error('Please upload a PDF file');
            return;
        }

        setUploading(true);
        try {
            const response = await uploadAsset(file);
            setFormData((prev) => ({ ...prev, url: response.data.url }));
            toast.success('File uploaded successfully');
        } catch (error) {
            console.error('Upload failed:', error);
            toast.error('Failed to upload file');
        } finally {
            setUploading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (formData.contentType === 'PDF' && !formData.url) {
            toast.error('Please upload a PDF file or provide a URL');
            return;
        }

        setFormLoading(true);
        try {
            const payload = {
                type: 'LEARNING',
                ...formData
            };

            if (editingItem) {
                await updateContent(editingItem.PK, editingItem.SK, payload);
                toast.success('Learning unit updated successfully');
            } else {
                await createContent(payload);
                toast.success('Learning unit created successfully');
            }
            setDialogOpen(false);
            fetchLearningData();
        } catch (error) {
            console.error('Failed to save learning unit:', error);
            toast.error('Failed to save learning unit');
        } finally {
            setFormLoading(false);
        }
    };

    const handleDelete = async (item) => {
        if (!window.confirm(`Are you sure you want to delete "${item.title}"?`)) return;

        try {
            await deleteContent(item.PK, item.SK);
            toast.success('Learning unit deleted successfully');
            fetchLearningData();
        } catch (error) {
            console.error('Failed to delete learning unit:', error);
            toast.error('Failed to delete learning unit');
        }
    };

    if (loading && learningItems.length === 0) {
        return (
            <div className="p-6 md:p-12 flex items-center justify-center min-h-[400px]">
                <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
            </div>
        );
    }

    return (
        <div className="p-6 md:p-12" data-testid="admin-learning-page">
            <div className="max-w-6xl">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                    <div>
                        <h1 className="font-heading text-3xl font-bold text-gray-900">Learning Materials</h1>
                        <p className="text-gray-600 mt-1">Manage educational units, categories and modules.</p>
                    </div>
                    <Button className="bg-amber-600 hover:bg-amber-700" onClick={handleOpenAdd} data-testid="add-learning-btn">
                        <Plus className="h-4 w-4 mr-2" />
                        Add Learning Unit
                    </Button>
                </div>

                {/* Search */}
                <Card className="border-gray-100 mb-6">
                    <CardContent className="p-4">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <Input
                                placeholder="Search units, categories or modules..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-9"
                                data-testid="learning-search-input"
                            />
                        </div>
                    </CardContent>
                </Card>

                {/* Table */}
                <Card className="border-gray-100">
                    <CardContent className="p-0">
                        <Table>
                            <TableHeader>
                                <TableRow className="bg-gray-50">
                                    <TableHead>Title</TableHead>
                                    <TableHead>Category</TableHead>
                                    <TableHead>Module</TableHead>
                                    <TableHead>Type</TableHead>
                                    <TableHead className="text-right">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filteredItems.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                                            <GraduationCap className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                                            No learning materials found
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    filteredItems.map((item) => (
                                        <TableRow key={item.id} data-testid={`learning-row-${item.id}`}>
                                            <TableCell className="font-medium">{item.title}</TableCell>
                                            <TableCell className="text-sm text-gray-600">{item.category}</TableCell>
                                            <TableCell className="text-sm text-gray-600">{item.module}</TableCell>
                                            <TableCell>
                                                <span className={`text-xs px-2 py-1 rounded ${item.contentType === 'PDF' || item.url ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'
                                                    }`}>
                                                    {item.contentType || (item.url ? 'PDF' : 'TEXT')}
                                                </span>
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <div className="flex justify-end gap-1">
                                                    <Button variant="ghost" size="sm" onClick={() => handleOpenEdit(item)}>
                                                        <Edit2 className="h-4 w-4" />
                                                    </Button>
                                                    <Button variant="ghost" size="sm" onClick={() => handleDelete(item)} className="text-red-600 hover:text-red-700">
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
            </div>

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>{editingItem ? 'Edit Learning Unit' : 'Add New Learning Unit'}</DialogTitle>
                        <DialogDescription>
                            Enter the details for the learning unit. Units will be automatically grouped by Category and Module in the member view.
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleSubmit}>
                        <div className="space-y-4 py-4">
                            <div className="space-y-2">
                                <Label htmlFor="title">Unit Title</Label>
                                <Input
                                    id="title"
                                    value={formData.title}
                                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                    placeholder="e.g. History of the Craft"
                                    required
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="category">Category (e.g. Year 1 - Foundation)</Label>
                                    <Input
                                        id="category"
                                        value={formData.category}
                                        onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                                        placeholder="Enter category name"
                                        required
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="module">Module (e.g. Introduction to Freemasonry)</Label>
                                    <Input
                                        id="module"
                                        value={formData.module}
                                        onChange={(e) => setFormData({ ...formData, module: e.target.value })}
                                        placeholder="Enter module name"
                                        required
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="contentType">Content Type</Label>
                                <Select
                                    value={formData.contentType}
                                    onValueChange={(val) => setFormData({ ...formData, contentType: val })}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select type" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="TEXT">Plain Text / HTML</SelectItem>
                                        <SelectItem value="PDF">PDF Document</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            {formData.contentType === 'PDF' ? (
                                <div className="space-y-2">
                                    <Label htmlFor="pdf-upload">Upload PDF Document</Label>
                                    <div className="flex gap-2">
                                        <div className="relative flex-1">
                                            <Input
                                                id="url"
                                                value={formData.url}
                                                onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                                                placeholder="https://example.com/material.pdf"
                                                required
                                                className="pr-10"
                                            />
                                            {formData.url && !uploading && (
                                                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                                    <FileText className="h-4 w-4 text-emerald-600" />
                                                </div>
                                            )}
                                        </div>
                                        <Label
                                            htmlFor="file-upload"
                                            className={`flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 cursor-pointer ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                                        >
                                            {uploading ? (
                                                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                            ) : (
                                                <Upload className="h-4 w-4 mr-2 text-amber-600" />
                                            )}
                                            {uploading ? 'Uploading...' : 'Upload'}
                                        </Label>
                                        <input
                                            id="file-upload"
                                            type="file"
                                            className="hidden"
                                            accept=".pdf"
                                            onChange={handleFileUpload}
                                            disabled={uploading}
                                        />
                                    </div>
                                    <p className="text-xs text-gray-500">
                                        Upload a PDF from your computer or paste a direct URL.
                                    </p>
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    <Label htmlFor="content">Unit Content (HTML supported)</Label>
                                    <Textarea
                                        id="content"
                                        value={formData.content}
                                        onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                                        placeholder="Enter the learning content here..."
                                        rows={8}
                                        required
                                    />
                                </div>
                            )}
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                            <Button type="submit" disabled={formLoading}>
                                {formLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                {editingItem ? 'Update Unit' : 'Save Unit'}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default AdminLearningPage;
